import type { CSSObject } from '@emotion/react'
import {
  Color,
  FocusIndicator,
  Size,
  Spacing,
  Transition,
  Typography,
} from '@opallabs/gem'
import { TabSize } from '../Tabs'
import type { StyleProps } from './types'

export const container = ({
  size,
  tabCount,
  theme,
}: StyleProps): CSSObject => ({
  backgroundColor: Color[theme].Translucent.A00,
  color: Color[theme].Text.Default,
  ...Typography.T500,
  alignItems: 'center',
  borderRadius: Size.Rounding,
  borderStyle: 'solid',
  borderWidth: 0,
  boxSizing: 'border-box',
  cursor: 'pointer',
  display: 'flex',
  flexGrow: 1,
  justifyContent: 'center',
  letterSpacing: 'inherit',
  paddingBlock: size === TabSize.Regular ? Spacing.XS * 1.25 : Spacing.XS / 4,
  paddingInline: Spacing.S,
  whiteSpace: 'nowrap',
  zIndex: 0,
  ...Transition([
    'background-color',
    'border-color',
    'box-shadow',
    'color',
    'opacity',
  ]),

  '&[aria-selected=true], &:not(:disabled):active': {
    backgroundColor: Color[theme].Surface.A200,
    color: Color.light.Surface.N000,
    zIndex: tabCount - 1,
  },

  '&:disabled': {
    cursor: 'not-allowed',
    opacity: 0.5,
  },

  '&:not(:disabled):focus': {
    ...FocusIndicator[theme],
    zIndex: tabCount,
  },

  '&:not(:disabled):focus, &:not(:disabled):hover': {
    backgroundColor:
      theme === 'light'
        ? Color[theme].Surface.A200
        : Color[theme].Translucent.A16,
    color: Color.light.Surface.N000,
  },

  '&[aria-selected=true]:hover, &:not(:disabled):active': {
    backgroundColor: Color[theme].Surface.A200,
    color: Color.light.Surface.N000,
  },

  '&[aria-selected=false]:hover, &:not(:disabled):active': {
    backgroundColor: Color[theme].Translucent.A16,
    color: Color[theme].Text.Accent,
  },

  '@media (prefers-contrast: more)': {
    borderColor: Color[theme].Surface.N600,
    color: Color[theme].Text.Default,
    borderWidth: 1,

    '&[aria-selected=true], &:not(:disabled):active': {
      backgroundColor: Color[theme].Surface.A200,
      color: Color.light.Surface.N000,
    },

    '&:hover': {
      backgroundColor:
        theme === 'light'
          ? Color[theme].Translucent.A16
          : Color[theme].Translucent.A24,
      color: Color[theme].Text.Default,
    },
  },
})
